import { useLayoutEffect, useState } from 'react';

import { useFreshRef } from 'src/lib/hooks/useFreshRef';

export const focusElement = (element: HTMLElement | null): void => {
  if (!element) return;

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'start',
  });
  element.focus({ preventScroll: true });
};

export const focusInputByName = (inputName: string): void => {
  const input = document.querySelector(
    `input[name=${inputName}], textarea[name=${inputName}]`
  ) as HTMLElement | null;

  focusElement(input);
};

export const useDOMElement = (
  rootElement: HTMLElement | null,
  query: string | null
): HTMLElement | null => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const elementRef = useFreshRef<HTMLElement | null>(element);
  const [currentQuery, setCurrentQuery] = useState<string | null>(null);

  // Observe "element" being removed from DOM
  useLayoutEffect(() => {
    if (!rootElement) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      if (elementRef.current && !document.body.contains(elementRef.current)) {
        setElement(null);
      }
    });

    observer.observe(rootElement, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [rootElement, elementRef]);

  // Listen to "query" and "element" changes
  useLayoutEffect(() => {
    if (element && !query) {
      return undefined;
    }

    if (!rootElement || !query) {
      setElement(null);
      return undefined;
    }

    // Search for DOM target element
    const target = rootElement.querySelector(query) as HTMLElement | null;
    if (target) {
      setElement(target);
      setCurrentQuery(query);
      return undefined;
    }

    // If no element was found right away, run searching periodically
    setElement(null);
    const interval = setInterval(() => {
      const targetInterval = rootElement.querySelector(query) as HTMLElement | null;
      if (targetInterval) {
        setElement(targetInterval);
        setCurrentQuery(query);

        clearInterval(interval);
      }
    }, 50);

    return () => {
      clearInterval(interval);
    };
  }, [rootElement, query, element]);

  if (currentQuery && currentQuery !== query) {
    return null;
  }
  return element;
};

export const getDomFirstSelectionRange = (): Range | null => {
  const domSelection = window.getSelection();
  if (!domSelection || domSelection.rangeCount === 0) {
    return null;
  }

  return domSelection.getRangeAt(0);
};

/**
 * Calculates current selection rects
 * @returns {{startRect: DOMRect, wholeRect: DOMRect, endRect: DOMRect}|undefined} - Selection rects
 */
export const getSelectionRects = () => {
  const sel = window.getSelection();
  if (!sel?.rangeCount) {
    return;
  }

  const range = sel.getRangeAt(0).cloneRange();
  if (!range) {
    return;
  }

  const rects = [...range.getClientRects()];
  const startRect = rects[0];
  const endRect = rects[rects.length - 1];
  if (!startRect || !endRect) {
    return;
  }

  const width = rects.reduce((acc, rect) => Math.max(acc, rect.width), 0);
  const height = rects.reduce((acc, rect) => acc + rect.height, 0);
  const wholeRect = {
    top: Math.min(startRect.top, endRect.top),
    left: Math.min(startRect.left, endRect.left),
    height,
    width,
  };

  return {
    startRect,
    endRect,
    wholeRect,
  };
};

export const clearAllDomSelection = (): void => {
  const domSelection = window.getSelection();
  if (!domSelection || domSelection.rangeCount === 0) {
    return;
  }

  domSelection.removeAllRanges();
};
